import { useEffect, useRef, useState } from 'react';
import { CursorPosition, Placement, TooltipTargetRect } from '../Tooltip.types';
import { getTooltipPosition } from '../utils';

interface UseTooltipPositionParams {
  placement: Placement;
  shouldRender: boolean;
  anchorToCursor: boolean;
  interactive: boolean;
  targetRef: React.RefObject<HTMLElement>;
  tooltipRef: React.RefObject<HTMLElement>;
  changeTooltipVisibility: (visible: boolean) => void;
}

interface TooltipPosition {
  top: number;
  left: number;
}

interface ResolveTargetRectParams {
  target: HTMLElement;
  cursor: CursorPosition | null;
}

const initialPosition: TooltipPosition = {
  top: -9999,
  left: -9999,
};

const resolveTargetRect = ({ target, cursor }: ResolveTargetRectParams): TooltipTargetRect => {
  if (cursor) {
    return {
      top: cursor.y,
      bottom: cursor.y,
      left: cursor.x,
      right: cursor.x,
      width: 0,
      height: 0,
    };
  }

  const triggerEl = (target.firstElementChild as HTMLElement) ?? target;

  return triggerEl.getBoundingClientRect();
};

export const useTooltipPosition = ({
  placement,
  shouldRender,
  anchorToCursor,
  interactive,
  targetRef,
  tooltipRef,
  changeTooltipVisibility,
}: UseTooltipPositionParams) => {
  const [position, setPosition] = useState<TooltipPosition>(initialPosition);
  const cursorRef = useRef<CursorPosition | null>(null);

  const trackCursor = (event: React.MouseEvent) => {
    cursorRef.current = { x: event.clientX, y: event.clientY };
  };

  useEffect(() => {
    if (!shouldRender) {
      cursorRef.current = null;
      return;
    }

    const target = targetRef.current;
    const tooltip = tooltipRef.current;

    if (!target || !tooltip) {
      return;
    }

    const updatePosition = () => {
      const targetRect = resolveTargetRect({
        target,
        cursor: anchorToCursor ? cursorRef.current : null,
      });

      setPosition(getTooltipPosition(targetRect, tooltip, placement));
      changeTooltipVisibility(true);
    };

    updatePosition();

    if (!interactive) {
      return;
    }

    const observer = new ResizeObserver(updatePosition);
    observer.observe(tooltip);

    return () => observer.disconnect();
  }, [shouldRender, placement, anchorToCursor, interactive]);

  return { position, trackCursor: anchorToCursor ? trackCursor : undefined };
};
